import { formatSessionTimeRemaining } from 'src/modules/communications/utils/whatsapp/format-session-time-remaining.util';
import { ClockIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { getExpirationInfo } from 'src/modules/shared/utils/expiration.util';
import { HOUR_IN_MS } from 'src/utils/date';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { formatTimeDistance } from 'src/modules/shared/utils/formatting/date.util';
import { usePeriodicRerender } from 'src/modules/shared/hooks/render';
import type { WhatsAppExpirationAlertProps } from 'src/modules/communications/types/whatsapp/expiration-alert.types';

/** Compact session status with an explanation available by pointer, focus or tap. */
export const WhatsAppExpirationAlert = ({
  channelSession,
  loading,
}: WhatsAppExpirationAlertProps) => {
  const { t, language } = useI18n();
  usePeriodicRerender(60_000);
  const endTime = channelSession?.endTime;
  const { status, timeRemaining } = getExpirationInfo(endTime);
  if (loading) {
    return (
      <div
        role="status"
        aria-label={t('shared.loading')}
        className="flex h-7 w-36 max-w-full items-center px-2 [@container(max-height:280px)]:h-6"
      >
        <TextSkeleton variant="neutral" />
      </div>
    );
  }

  const timeLeft =
    endTime && status === 'active'
      ? formatTimeDistance(new Date(endTime), false, language)
      : '';
  const label =
    status === 'active'
      ? t('communications.whatsappExpirationAlert.remaining', {
          timeLeft: formatSessionTimeRemaining(timeRemaining ?? 0, language),
        })
      : t(
          status === 'expired'
            ? 'communications.whatsappExpirationAlert.expired.label'
            : 'communications.whatsappExpirationAlert.notActive.label'
        );
  const explanation =
    status === 'active'
      ? `${t('communications.whatsappExpirationAlert.active', { timeLeft })} ${t('communications.whatsappExpirationAlert.warning.description')}`
      : t(
          status === 'expired'
            ? 'communications.whatsappExpirationAlert.expired.description'
            : 'communications.whatsappExpirationAlert.notActive.description'
        );
  const variant =
    status === 'active' &&
    timeRemaining !== null &&
    timeRemaining <= 3 * HOUR_IN_MS
      ? 'warning'
      : 'secondary';

  return (
    <Alert
      appearance="ghost"
      variant={variant}
      size="2xs"
      className="!p-0"
      message={
        <Tooltip
          interactive
          as="span"
          size="sm"
          className="max-w-[min(20rem,calc(100vw-2rem))]"
          content={explanation}
        >
          <Button
            appearance="ghost"
            variant={variant}
            size="2xs"
            className="[@container(max-height:280px)]:!h-6"
            aria-label={`${label}. ${t('communications.whatsappExpirationAlert.explanation')}`}
          >
            <ClockIcon className="h-4 w-4 shrink-0" />
            <Text as="span" size="tiny">
              {label}
            </Text>
            <InformationCircleIcon className="h-4 w-4 shrink-0" />
          </Button>
        </Tooltip>
      }
    />
  );
};
